import React, { useCallback, useEffect, useState, useRef } from "react";
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  Search,
  Calendar,
  User,
  Loader2,
} from "lucide-react";
import BlogForm from "../Components/BlogForm";
import { getBlogsAPI, deleteBlogAPI } from "../services/apicollection";

const GENRES = [
  "Web Development",
  "AI & Machine Learning",
  "Mobile Development",
  "DevOps",
  "UI/UX Design",
  "Data Science",
  "Cybersecurity",
  "Cloud Computing",
  "Blockchain",
  "Backend Development",
  "Digital Marketing",
  "Data Analytics",
  "Dsa",
  "Full Stack Development",
];

function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

const BlogManagementPage = () => {
  const [blogs, setBlogs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterGenre, setFilterGenre] = useState("");
  const [sortBy, setSortBy] = useState("date");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  
  // Use useRef for AbortController to avoid dependency issues
  const controllerRef = useRef(null);

  const fetchBlogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Use the ref's current controller
      const res = await getBlogsAPI({ signal: controllerRef.current?.signal });
      if (res?.status !== false && Array.isArray(res?.data)) {
        setBlogs(res.data);
      } else {
        setBlogs([]);
      }
    } catch (err) {
      // Ignore abort errors
      if (err.name !== 'AbortError') {
        setError(err.message || "Failed to load blogs");
      }
      setBlogs([]);
    } finally {
      setLoading(false);
    }
  }, []); // No dependencies needed since controllerRef is stable

  useEffect(() => {
    // Create new AbortController when component mounts
    controllerRef.current = new AbortController();
    
    fetchBlogs();

    // Cleanup function to abort any ongoing requests
    return () => {
      if (controllerRef.current) {
        controllerRef.current.abort();
        controllerRef.current = null;
      }
    };
  }, [fetchBlogs]);

  const handleSave = () => {
    setShowForm(false);
    setEditingBlog(null);
    fetchBlogs();
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingBlog(null);
  };

  const handleEdit = (blog) => {
    setEditingBlog(blog);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this blog? This cannot be undone.")) return;
    setDeletingId(id);
    try {
      const res = await deleteBlogAPI(id);
      if (res?.status === false) throw new Error(res.message);
      await fetchBlogs();
    } catch (err) {
      alert(err.message || "Failed to delete blog");
    } finally {
      setDeletingId(null);
    }
  };

  const filteredBlogs = [...blogs]
    .filter((blog) => {
      const term = (searchTerm || "").toLowerCase();
      const matchesSearch =
        !term ||
        (blog.title || "").toLowerCase().includes(term) ||
        (blog.desc || "").toLowerCase().includes(term) ||
        (blog.author || "").toLowerCase().includes(term);
      const matchesGenre = !filterGenre || blog.genre === filterGenre;
      return matchesSearch && matchesGenre;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "date":
          return (
            new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime()
          );
        case "title":
          return (a.title || "").localeCompare(b.title || "");
        case "author":
          return (a.author || "").localeCompare(b.author || "");
        case "views":
          return parseInt(b.views || 0, 10) - parseInt(a.views || 0, 10);
        default:
          return 0;
      }
    });

  if (showForm) {
    return (
      <BlogForm
        onSave={handleSave}
        onCancel={handleCancel}
        initialData={editingBlog ?? undefined}
        isEditing={!!editingBlog}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-200">Blogs</h2>
        <button
          type="button"
          onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-slate-800 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-slate-700"
        >
          <Plus size={16} />
          New blog
        </button>
      </div>

      <div className="rounded-lg border border-[#1d3350] bg-[#0f1c2e] p-4">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Search
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-lg border border-[#1d3350] py-2 pl-9 pr-3 text-sm focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400"
            />
          </div>
          <div className="flex gap-2">
            <select
              value={filterGenre}
              onChange={(e) => setFilterGenre(e.target.value)}
              className="rounded-lg border border-[#1d3350] px-3 py-2 text-sm focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400"
            >
              <option value="">All genres</option>
              {GENRES.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="rounded-lg border border-[#1d3350] px-3 py-2 text-sm focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400"
            >
              <option value="date">Date</option>
              <option value="title">Title</option>
              <option value="author">Author</option>
              <option value="views">Views</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={28} className="animate-spin text-slate-400" />
          </div>
        ) : error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-4 text-center text-sm text-red-700">
            {error}
          </div>
        ) : filteredBlogs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Calendar size={40} className="text-slate-300" />
            <p className="mt-2 text-sm text-slate-400">
              {searchTerm || filterGenre
                ? "No blogs match your filters."
                : "No blogs yet."}
            </p>
            {!searchTerm && !filterGenre && (
              <button
                type="button"
                onClick={() => setShowForm(true)}
                className="mt-2 text-sm font-medium text-slate-200 underline hover:no-underline"
              >
                Create first blog
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filteredBlogs.map((blog) => (
              <article
                key={blog._id}
                className="flex flex-col overflow-hidden rounded-lg border border-[#1d3350] bg-[#0f1c2e] shadow-sm transition-shadow hover:shadow"
              >
                {blog.poster && (
                  <div className="h-40 overflow-hidden">
                    <img
                      src={
                        typeof blog.poster === "string"
                          ? blog.poster
                          : blog.poster?.url
                      }
                      alt={blog.title}
                      className="h-full w-full object-cover"
                    />
                  </div>
                )}
                <div className="flex min-h-0 flex-1 flex-col p-3">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <span className="rounded-full bg-slate-800 px-2 py-0.5 text-[10px] font-medium text-white">
                      {blog.genre || "—"}
                    </span>
                    <span className="text-[10px] text-slate-500">
                      {blog.level || ""}
                    </span>
                  </div>
                  <h3 className="mb-1 line-clamp-2 text-sm font-semibold text-slate-100">
                    {blog.title}
                  </h3>
                  <p className="mb-3 line-clamp-2 text-xs text-slate-400">
                    {blog.desc}
                  </p>
                  <div className="mb-3 flex items-center gap-3 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <User size={12} />
                      {blog.author}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar size={12} />
                      {formatDate(blog.date)}
                    </span>
                  </div>
                  {Array.isArray(blog.tags) && blog.tags.length > 0 && (
                    <div className="mb-3 flex flex-wrap gap-1">
                      {blog.tags.slice(0, 3).map((tag, i) => (
                        <span
                          key={i}
                          className="rounded bg-[#13233a] px-2 py-0.5 text-[10px] text-slate-400"
                        >
                          {tag}
                        </span>
                      ))}
                      {blog.tags.length > 3 && (
                        <span className="text-[10px] text-slate-400">
                          +{blog.tags.length - 3}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="mt-auto flex items-center justify-between border-t border-[#17283f] pt-2">
                    <span className="flex items-center gap-1 text-[10px] text-slate-500">
                      <Eye size={12} />
                      {blog.views ?? 0} views
                    </span>
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => handleEdit(blog)}
                        className="rounded p-2 text-slate-400 hover:bg-[#13233a] hover:text-slate-200"
                        title="Edit"
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(blog._id)}
                        disabled={deletingId === blog._id}
                        className="rounded p-2 text-red-600 hover:bg-red-50 hover:text-red-700 disabled:opacity-50"
                        title="Delete"
                      >
                        {deletingId === blog._id ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <Trash2 size={14} />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogManagementPage;